import { useEffect, useRef, useState } from 'react'
import isEqual from 'lodash-es/isEqual'

import { type Filter } from '@rewardops/api'
import queryBuilder from '@rewardops/api/src/query-builder'
import { useMemberTags, useSegmentCode } from '@rewardops/auth/src/client'
import { useLocale } from '@rewardops/ui'
import { type ResponseItem } from '@rewardops/utils'

import { useApi } from './use-api'

export const useItems = (filter: Filter, page: number, pageSize?: number) => {
  const [error, setError] = useState(false)
  const [items, setItems] = useState([] as ResponseItem[])
  const [outsideLoading, setOutsideLoading] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const segmentCode = useSegmentCode()
  const memberTags = useMemberTags()
  const { locale } = useLocale()
  const { api, tenant } = useApi()

  const prevFilter = useRef<Filter | null>(filter)

  const {
    data: itemResponse,
    isLoading: loading,
    error: itemsError,
  } = api.items.getAll.useQuery(
    // TODO: Replace getAllWithFilter with getAll
    queryBuilder.items.getAllWithFilter(filter, tenant, {
      segment_code: segmentCode,
      accept_language: locale,
      ...(memberTags && { member_tags: memberTags }),
      page,
      pageSize,
    }),
  )

  useEffect(() => {
    setError(false)

    if (itemResponse && !('error' in itemResponse.result)) {
      // in case of filter changes, reset items entirely
      if (!isEqual(prevFilter.current, filter)) {
        prevFilter.current = filter
        setItems([...itemResponse.result.items])
      } else {
        setItems((prevItems) => {
          const existingIds = new Set(prevItems.map((item) => item.id))
          const newItems = itemResponse.result.items.filter(
            (item: ResponseItem) => !existingIds.has(item.id),
          )
          return [...prevItems, ...newItems]
        })
      }

      if (itemsError) {
        setError(true)
        console.error(itemsError)
      }

      setOutsideLoading(false)
      setHasMore(
        !itemResponse.pagination ||
          itemResponse.pagination.current < itemResponse.pagination.pages,
      )
    }
  }, [itemResponse, itemsError])

  return {
    loading: outsideLoading,
    error,
    items,
    hasMore,
    totalItems: itemResponse?.pagination?.count,
  }
}
